import { Graphics } from 'pixi.js';
import type { TelegraphSystem } from '../sim/combat/TelegraphSystem';
import { Palette } from './Palette';

/**
 * Draws telegraph warnings: zones fill radially inward over the lead time (a
 * readable countdown) then flash white at commit; lines pulse alpha.
 * Shared Graphics redrawn per frame — dozens of shapes, cheap.
 */
export class TelegraphRenderer {
  readonly gfx = new Graphics();

  constructor(private readonly system: TelegraphSystem) {}

  sync(): void {
    const g = this.gfx;
    g.clear();
    for (const t of this.system.list) {
      const progress = 1 - Math.max(0, t.left) / Math.max(1, t.total);
      if (t.left <= 0) {
        // Commit flash. Debris lands brown, not white (2026-07-28 rework).
        if (t.shape === 'zone') {
          const col = t.style === 'debris' ? Palette.debris : 0xffffff;
          g.circle(t.x, t.y, t.radius).fill({ color: col, alpha: 0.55 + t.left * 0.08 });
        }
        continue;
      }
      if (t.shape === 'zone' && t.style === 'debris') {
        // Falling rubble: a big brown circle that appears and darkens, with a
        // GROWING inner shadow (the boulder coming down) — deliberately
        // nothing like the pink enemy-telegraph language.
        g.circle(t.x, t.y, t.radius).fill({ color: Palette.debris, alpha: 0.20 + 0.30 * progress });
        g.circle(t.x, t.y, t.radius).stroke({ width: 3, color: Palette.debrisDeep, alpha: 0.8 });
        g.circle(t.x, t.y, t.radius * progress).fill({ color: Palette.debrisDeep, alpha: 0.45 });
      } else if (t.shape === 'zone') {
        g.circle(t.x, t.y, t.radius).fill({ color: Palette.telegraph, alpha: 0.13 });
        g.circle(t.x, t.y, t.radius).stroke({ width: 2, color: Palette.telegraph, alpha: 0.7 });
        // Inward fill: shrinking rim closes on the center as commit approaches.
        g.circle(t.x, t.y, t.radius * (1 - progress)).stroke({
          width: 2.5, color: Palette.telegraph, alpha: 0.9,
        });
      } else {
        const pulse = 0.1 + 0.1 * Math.sin(progress * Math.PI * 6);
        const dx = Math.cos(t.angle);
        const dy = Math.sin(t.angle);
        const nx = -dy * (t.width / 2);
        const ny = dx * (t.width / 2);
        g.poly([
          t.x + nx, t.y + ny,
          t.x + dx * t.length + nx, t.y + dy * t.length + ny,
          t.x + dx * t.length - nx, t.y + dy * t.length - ny,
          t.x - nx, t.y - ny,
        ]).fill({ color: Palette.telegraph, alpha: pulse });
      }
    }
  }
}
